import React, { useContext, useEffect, useRef, useState } from "react";
import { X, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { LanguageContext } from "../../constant/LanguageContext";
import { translations } from "./translations";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const INITIAL = { name: "", company: "", email: "", phone: "", message: "", website: "" };

export default function DemoRequestModal({ open, onClose }) {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const firstFieldRef = useRef(null);
  const openerRef = useRef(null);

  const { language } = useContext(LanguageContext);
  const t = (translations[language] || translations.en).modal;
  const dir = language === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    if (!open) return;
    // Remember who opened the dialog so focus can go back there on close.
    openerRef.current = document.activeElement;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    firstFieldRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${BASE_URL}/api/public/demo-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t.genericError);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || t.genericError);
    }
  };

  const closeAndReset = () => {
    onClose();
    setTimeout(() => {
      setForm(INITIAL);
      setStatus("idle");
      setErrorMsg("");
    }, 250);
  };

  return (
    <div className="nxl-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && closeAndReset()}>
      <div className="nxl-modal" role="dialog" aria-modal="true" aria-labelledby="nxl-demo-title" dir={dir} lang={language}>
        <div className="nxl-modal-inner">
        <button className="nxl-modal-close" onClick={closeAndReset} aria-label={t.close}>
          <X size={20} />
        </button>

        {status === "success" ? (
          <div className="nxl-modal-result">
            <CheckCircle2 size={52} className="nxl-modal-ok" aria-hidden="true" />
            <h3 id="nxl-demo-title">{t.successTitle}</h3>
            <p>{t.successText}</p>
            <button className="nxl-btn nxl-btn-primary" onClick={closeAndReset}>{t.done}</button>
          </div>
        ) : (
          <>
            <h3 className="nxl-modal-title" id="nxl-demo-title">{t.title}</h3>
            <p className="nxl-modal-sub">{t.sub}</p>
            <form onSubmit={submit} className="nxl-modal-form">
              <div className="nxl-modal-grid">
                <label>
                  {t.name}
                  <input ref={firstFieldRef} type="text" value={form.name} onChange={set("name")} required minLength={2} placeholder={t.namePh} />
                </label>
                <label>
                  {t.company}
                  <input type="text" value={form.company} onChange={set("company")} placeholder={t.companyPh} />
                </label>
                <label>
                  {t.email}
                  <input type="email" value={form.email} onChange={set("email")} required placeholder={t.emailPh} />
                </label>
                <label>
                  {t.phone}
                  <input type="tel" value={form.phone} onChange={set("phone")} required minLength={5} placeholder={t.phonePh} dir="ltr" />
                </label>
              </div>
              <label>
                {t.message}
                <textarea rows={3} value={form.message} onChange={set("message")} placeholder={t.messagePh} />
              </label>
              {/* Honeypot — hidden from real users */}
              <input
                type="text" value={form.website} onChange={set("website")}
                name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
              />
              {status === "error" && (
                <div className="nxl-modal-error">
                  <AlertCircle size={16} />
                  <span>
                    {errorMsg} {t.errorHint}{" "}
                    <a href="mailto:support@nexploy-eg.com">support@nexploy-eg.com</a>.
                  </span>
                </div>
              )}
              <button type="submit" className="nxl-btn nxl-btn-primary nxl-btn-lg nxl-modal-submit" disabled={status === "sending"}>
                {status === "sending" ? t.sending : (<>{t.submit} <Send size={16} /></>)}
              </button>
            </form>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
